import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../api/axios';
import AssessmentPayloadEditor from './AssessmentPayloadEditor';
import ProgramExamWizardModal from './ProgramExamWizardModal';
import ProgramExamAuditPanel from './ProgramExamAuditPanel';
import { PROGRAM_EXAM_TASK_CODE, filterProgramExamAssessments } from '../utils/taskAssessmentIacAudit';

function qCount(p) {
  return Array.isArray(p?.questions) ? p.questions.length : 0;
}

function marksTotal(p) {
  return Array.isArray(p?.questions)
    ? p.questions.reduce((s, q) => s + (Number(q.marks) || 0), 0)
    : 0;
}

export default function ProgramExamsPanel({
  qualificationId,
  qualification,
  assignedItems,
  parentListVersion,
  onListChanged
}) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editPayload, setEditPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = useCallback(async () => {
    if (!qualificationId) return;
    setLoading(true);
    try {
      const res = await api.get(`/qualifications/${qualificationId}/learning-content/task-assessments`, {
        params: { scope: 'program' }
      });
      setAssessments(res.data?.assessments || []);
    } catch (_) {
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [qualificationId]);

  useEffect(() => {
    load();
  }, [load, parentListVersion]);

  const programExams = useMemo(
    () => filterProgramExamAssessments(assessments),
    [assessments]
  );

  const openRow = (r) => {
    setSelected(r);
    setEditPayload(r.payload ? JSON.parse(JSON.stringify(r.payload)) : { questions: [] });
  };

  const closeEditor = () => {
    setSelected(null);
    setEditPayload(null);
  };

  const saveEdit = async (nextStatus) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(
        `/qualifications/${qualificationId}/learning-content/task-assessments/${selected.id}`,
        { payload: editPayload, status: nextStatus }
      );
      if (onListChanged) onListChanged();
      await load();
      closeEditor();
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async (id) => {
    try {
      await api.delete(`/qualifications/${qualificationId}/learning-content/task-assessments/${id}`);
      if (onListChanged) onListChanged();
      await load();
      if (selected?.id === id) closeEditor();
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const programTitle =
    qualification?.name ||
    qualification?.title ||
    qualification?.programme_name ||
    'Program';

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
        Exam Module (program)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Qualification: {programTitle}. Program exams draw on selected BoK tasks and required IACs across
        the programme. They are stored separately from per-module task assessments and module exams.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" color="primary" startIcon={<SchoolIcon />} onClick={() => setWizardOpen(true)}>
          New program exam
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading program exams…</Typography>
        </Box>
      ) : programExams.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No program exams yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Questions</TableCell>
              <TableCell>Marks</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {programExams.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  {r.task_code === PROGRAM_EXAM_TASK_CODE
                    ? r.config?.program_title || 'Program exam'
                    : r.task_code}
                </TableCell>
                <TableCell>{r.kind === 'mcq' ? 'MCQ' : 'Free text'}</TableCell>
                <TableCell>{qCount(r.payload)}</TableCell>
                <TableCell>{marksTotal(r.payload)}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.status} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => openRow(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setConfirmDeleteId(r.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ProgramExamAuditPanel assessments={assessments} />

      <ProgramExamWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        qualificationId={qualificationId}
        qualification={qualification}
        assignedItems={assignedItems}
        onSaved={async () => {
          if (onListChanged) onListChanged();
          await load();
          setWizardOpen(false);
        }}
      />

      <Dialog open={Boolean(selected)} onClose={closeEditor} maxWidth="lg" fullWidth>
        <DialogTitle>
          Edit program exam — {selected?.kind === 'mcq' ? 'MCQ' : 'Free text'}
        </DialogTitle>
        <DialogContent dividers>
          {selected && editPayload && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${qCount(editPayload)} questions`} />
                <Chip size="small" label={`${marksTotal(editPayload)} marks`} />
                <Chip size="small" label={`Status: ${selected.status}`} />
              </Stack>
              <AssessmentPayloadEditor
                kind={selected.kind}
                payload={editPayload}
                onChange={setEditPayload}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => selected && setConfirmDeleteId(selected.id)}
          >
            Delete
          </Button>
          <Button variant="outlined" disabled={saving} onClick={() => saveEdit('saved')}>
            Save
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => saveEdit('approved')}>
            Save and approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteId != null} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete program exam?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => performDelete(confirmDeleteId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
